import React, { useState, useEffect } from 'react';
import { useDropzone } from 'react-dropzone';
import { 
  User, Phone, FileText, Upload, Image, X, Eye
} from 'lucide-react';
import { BusinessCard, CardFormData } from '../../types';
import { cardServices } from '../../services/cardServices';
import { githubApi } from '../../services/githubApi';

interface CardFormProps {
  card?: BusinessCard;
  onSave: (card: BusinessCard) => void;
  onCancel: () => void;
  isLoading?: boolean;
}

const CardForm: React.FC<CardFormProps> = ({ 
  card, 
  onSave, 
  onCancel, 
  isLoading = false 
}) => {
  const [formData, setFormData] = useState<CardFormData>({
    name: '',
    slug: '',
    position: '',
    company: process.env.REACT_APP_COMPANY_NAME || 'Prime Utility Company',
    image: '',
    phone: '',
    whatsappMessage: '',
    email: '',
    website: '',
    linkedin: '',
    twitter: '',
    instagram: '',
    address: '',
    description: '',
    theme: 'modern',
    isMainCard: false,
  });

  const [errors, setErrors] = useState<string[]>([]);
  const [imagePreview, setImagePreview] = useState<string>('');
  const [isUploading, setIsUploading] = useState(false);

  useEffect(() => {
    if (card) {
      setFormData({
        name: card.name,
        slug: card.slug,
        position: card.position,
        company: card.company,
        image: card.image || '',
        phone: card.phone,
        whatsappMessage: card.whatsappMessage || '',
        email: card.email,
        website: card.website || '',
        linkedin: card.linkedin || '',
        twitter: card.twitter || '',
        instagram: card.instagram || '',
        address: card.address || '',
        description: card.description || '',
        theme: card.theme,
        isMainCard: card.isMainCard,
      });
      setImagePreview(card.image || '');
    }
  }, [card]);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    accept: {
      'image/*': ['.jpeg', '.jpg', '.png', '.gif', '.webp']
    },
    maxFiles: 1,
    onDrop: handleImageDrop
  });

  function handleImageDrop(acceptedFiles: File[]) {
    if (acceptedFiles.length > 0) {
      uploadImage(acceptedFiles[0]);
    }
  }

  const uploadImage = async (file: File) => {
    setIsUploading(true);
    try {
      const result = await githubApi.uploadImage(file);
      if (result.success && result.data) {
        setFormData(prev => ({ ...prev, image: result.data }));
        setImagePreview(result.data);
      } else {
        setErrors([result.error || 'Failed to upload image']);
      }
    } catch (error) {
      setErrors(['Failed to upload image']);
    } finally {
      setIsUploading(false);
    }
  };

  const handleInputChange = (field: keyof CardFormData, value: string | boolean) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    
    // Auto-generate slug from name
    if (field === 'name' && typeof value === 'string') {
      const slug = cardServices.generateSlug(value);
      setFormData(prev => ({ ...prev, slug }));
    }
    
    // Clear errors when user starts typing
    if (errors.length > 0) {
      setErrors([]);
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    // Validate form
    const validation = cardServices.validateCard(formData);
    if (!validation.isValid) {
      setErrors(validation.errors);
      return;
    }

    // Create card object
    const cardData: BusinessCard = {
      id: card?.id || formData.slug,
      slug: formData.slug,
      isMainCard: formData.isMainCard,
      name: formData.name,
      position: formData.position,
      company: formData.company,
      image: formData.image || undefined,
      phone: formData.phone,
      whatsappMessage: formData.whatsappMessage || undefined,
      email: formData.email,
      website: formData.website || undefined,
      linkedin: formData.linkedin || undefined,
      twitter: formData.twitter || undefined,
      instagram: formData.instagram || undefined,
      address: formData.address || undefined,
      description: formData.description || undefined,
      theme: formData.theme,
      createdAt: card?.createdAt || new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    };

    onSave(cardData);
  };

  const removeImage = () => {
    setFormData(prev => ({ ...prev, image: '' }));
    setImagePreview('');
  };

  const themeConfig = cardServices.getThemeConfig();

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      {/* Basic Information */}
      <div className="bg-white p-6 rounded-lg shadow-sm border">
        <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
          <User className="h-5 w-5 mr-2" />
          Basic Information
        </h3>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Full Name *
            </label>
            <input
              type="text"
              value={formData.name}
              onChange={(e) => handleInputChange('name', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-primary focus:border-primary"
              placeholder="Enter full name"
              required
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Slug *
            </label>
            <input
              type="text"
              value={formData.slug}
              onChange={(e) => handleInputChange('slug', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-primary focus:border-primary"
              placeholder="english-only-slug"
              required
            />
            <p className="text-xs text-gray-500 mt-1">
              English letters, numbers, and hyphens only
            </p>
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Position *
            </label>
            <input
              type="text"
              value={formData.position}
              onChange={(e) => handleInputChange('position', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-primary focus:border-primary"
              placeholder="e.g., General Manager"
              required
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Company
            </label>
            <input
              type="text"
              value={formData.company}
              onChange={(e) => handleInputChange('company', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-primary focus:border-primary"
              placeholder="Company name"
            />
          </div>
        </div>
      </div>

      {/* Contact Information */}
      <div className="bg-white p-6 rounded-lg shadow-sm border">
        <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
          <Phone className="h-5 w-5 mr-2" />
          Contact Information
        </h3>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Phone Number *
            </label>
            <input
              type="tel"
              value={formData.phone}
              onChange={(e) => handleInputChange('phone', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-primary focus:border-primary"
              placeholder="+966501234567"
              required
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Email *
            </label>
            <input
              type="email"
              value={formData.email}
              onChange={(e) => handleInputChange('email', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-primary focus:border-primary"
              placeholder="email@example.com"
              required
            />
          </div>
          
          <div className="md:col-span-2">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              WhatsApp Message
            </label>
            <textarea
              value={formData.whatsappMessage}
              onChange={(e) => handleInputChange('whatsappMessage', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-primary focus:border-primary"
              placeholder="Custom WhatsApp message for this card"
              rows={3}
            />
            <p className="text-xs text-gray-500 mt-1">
              This message will be sent when someone clicks WhatsApp
            </p>
          </div>
        </div>
      </div>

      {/* Image Upload */}
      <div className="bg-white p-6 rounded-lg shadow-sm border">
        <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
          <Image className="h-5 w-5 mr-2" />
          Profile Image
        </h3>
        
        {imagePreview ? (
          <div className="space-y-4">
            <div className="relative inline-block">
              <img
                src={imagePreview}
                alt="Preview"
                className="w-32 h-32 rounded-lg object-cover border"
              />
              <button
                type="button"
                onClick={removeImage}
                className="absolute -top-2 -right-2 p-1 bg-red-500 text-white rounded-full hover:bg-red-600"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
            <p className="text-sm text-gray-600">Image uploaded successfully</p>
          </div>
        ) : (
          <div
            {...getRootProps()}
            className={`border-2 border-dashed rounded-lg p-6 text-center cursor-pointer transition-colors ${
              isDragActive ? 'border-primary bg-primary bg-opacity-5' : 'border-gray-300 hover:border-primary'
            }`}
          >
            <input {...getInputProps()} />
            <Upload className="mx-auto h-12 w-12 text-gray-400" />
            <p className="mt-2 text-sm text-gray-600">
              {isDragActive ? 'Drop the image here' : 'Drag & drop an image, or click to select'}
            </p>
            <p className="text-xs text-gray-500 mt-1">
              PNG, JPG, GIF up to 5MB
            </p>
            {isUploading && (
              <div className="mt-2">
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-primary mx-auto"></div>
                <p className="text-xs text-gray-500">Uploading...</p>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Additional Information */}
      <div className="bg-white p-6 rounded-lg shadow-sm border">
        <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
          <FileText className="h-5 w-5 mr-2" />
          Additional Information
        </h3>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Website
            </label>
            <input
              type="url"
              value={formData.website}
              onChange={(e) => handleInputChange('website', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-primary focus:border-primary"
              placeholder="https://example.com"
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Address
            </label>
            <input
              type="text"
              value={formData.address}
              onChange={(e) => handleInputChange('address', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-primary focus:border-primary"
              placeholder="City, Country"
            />
          </div>
          
          <div className="md:col-span-2">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Description
            </label>
            <textarea
              value={formData.description}
              onChange={(e) => handleInputChange('description', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-primary focus:border-primary"
              placeholder="Brief description about yourself or your role"
              rows={3}
            />
          </div>
        </div>
      </div>

      {/* Social Media */}
      <div className="bg-white p-6 rounded-lg shadow-sm border">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Social Media</h3>
        
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              LinkedIn
            </label>
            <input
              type="text"
              value={formData.linkedin}
              onChange={(e) => handleInputChange('linkedin', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-primary focus:border-primary"
              placeholder="username (without link)"
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Twitter
            </label>
            <input
              type="text"
              value={formData.twitter}
              onChange={(e) => handleInputChange('twitter', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-primary focus:border-primary"
              placeholder="username (without @)"
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Instagram
            </label>
            <input
              type="text"
              value={formData.instagram}
              onChange={(e) => handleInputChange('instagram', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-primary focus:border-primary"
              placeholder="username (without @)"
            />
          </div>
        </div>
      </div>

      {/* Theme Selection */}
      <div className="bg-white p-6 rounded-lg shadow-sm border">
        <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
          <Eye className="h-5 w-5 mr-2" />
          Card Theme
        </h3>
        
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {themeConfig.map((theme) => (
            <div
              key={theme.name}
              className={`border-2 rounded-lg p-4 cursor-pointer transition-colors ${
                formData.theme === theme.name
                  ? 'border-primary bg-primary text-white'
                  : 'border-gray-200 hover:border-gray-300'
              }`}
              onClick={() => handleInputChange('theme', theme.name)}
            >
              <div className="text-center">
                <div className="text-2xl mb-2">{theme.preview}</div>
                <h4 className={`font-medium ${formData.theme === theme.name ? 'text-white' : 'text-gray-900'}`}>{theme.displayName}</h4>
                <p className={`text-sm mt-1 ${formData.theme === theme.name ? 'text-white text-opacity-80' : 'text-gray-600'}`}>{theme.description}</p>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Settings */}
      <div className="bg-white p-6 rounded-lg shadow-sm border">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Settings</h3>
        
        <div className="flex items-center">
          <input
            type="checkbox"
            id="isMainCard"
            checked={formData.isMainCard}
            onChange={(e) => handleInputChange('isMainCard', e.target.checked)}
            className="h-4 w-4 text-primary focus:ring-primary border-gray-300 rounded"
          />
          <label htmlFor="isMainCard" className="ml-2 block text-sm text-gray-900">
            Set as main card (this will be the default card shown on the homepage)
          </label>
        </div>
      </div>

      {/* Errors */}
      {errors.length > 0 && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <h4 className="text-sm font-medium text-red-800 mb-2">Please fix the following errors:</h4>
          <ul className="text-sm text-red-700 space-y-1">
            {errors.map((error, index) => (
              <li key={index}>• {error}</li>
            ))}
          </ul>
        </div>
      )}

      {/* Form Actions */}
      <div className="flex justify-end space-x-4">
        <button
          type="button"
          onClick={onCancel}
          className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 transition-colors"
          disabled={isLoading}
        >
          Cancel
        </button>
        <button
          type="submit"
          disabled={isLoading}
          className="px-4 py-2 bg-primary text-white rounded-md hover:bg-primary-dark transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {isLoading ? 'Saving...' : card ? 'Update Card' : 'Create Card'}
        </button>
      </div>
    </form>
  );
};

export default CardForm; 